import { SelectItem } from 'primeng/api';
import { StrtInitiative } from './plan/StrtInitiative';
import { StrtPriority } from './plan/StrtPriority';
import { StrtObjective } from './plan/StrtObjective';


export interface IPriorityDropdownParentIds {
  planId: number;
  priorityId?: number;
  objectiveId?: number;
  initiativeId?: number;
  keyMeasureId?: number;
}

export interface IPriorityDropdownOptionValue {
  value: any;
  planId: number;
  primaryId: number;
  htmlContent?: string;
  colorCode?: string;
  startDate?: string;
  endDate?: string;
  parentIds: IPriorityDropdownParentIds;
}

export interface IPriorityDropdownOption extends SelectItem {
  label?: string;
  value: IPriorityDropdownOptionValue;
  styleClass?: string;
  icon?: string;
  title?: string;
}

export class PriorityDropdownOption implements IPriorityDropdownOption {
  label?: string;
  value: IPriorityDropdownOptionValue;
  styleClass?: string;
  icon?: string;
  title?: string;

  constructor(options: IPriorityDropdownOption) {
    this.label = options.label;
    this.value = options.value;
    this.styleClass = options.styleClass;
    this.icon = options.icon;
    this.title = options.title;
    this.value.primaryId = options.value.primaryId;
    this.value.htmlContent = options.value.htmlContent;
    this.value.colorCode = options.value.colorCode;
    this.value.startDate = options.value.startDate;
    this.value.endDate = options.value.endDate;
    this.value.parentIds = options.value.parentIds;
  }

  public static fromInitiative(initiative: StrtInitiative, objective: StrtObjective, priority: StrtPriority, boldedTitle: boolean = true)
    : PriorityDropdownOption {
    const parentIds: IPriorityDropdownParentIds = {
      planId: 1,
      priorityId: priority.priorityId,
      objectiveId: objective.objectiveId,
      initiativeId: initiative.initiativeId
    };
    const content = boldedTitle ? `<strong>${priority.title}</strong> ${initiative.description}` : initiative.description;
    const value: IPriorityDropdownOptionValue = {
      planId: 1,
      primaryId: initiative.initiativeId,
      colorCode: priority.colorCode,
      htmlContent: content,
      startDate: PriorityDropdownOption.formattedDate(initiative.startDate),
      endDate: PriorityDropdownOption.formattedDate(initiative.endDate),
      value: initiative.initiativeId,
      parentIds: parentIds
    };
    return new PriorityDropdownOption({
      label: initiative.description,
      title: priority.title,
      value: value
    });
  }

  public static fromObjective(objective: StrtObjective, priority: StrtPriority, boldedTitle: boolean = true): PriorityDropdownOption {
    const parentIds: IPriorityDropdownParentIds = {
      planId: 1,
      priorityId: priority.priorityId,
      objectiveId: objective.objectiveId
    };
    const content = boldedTitle ? `<strong>${priority.title}</strong> ${objective.text}` : objective.text;
    const value: IPriorityDropdownOptionValue = {
      planId: 1,
      primaryId: objective.objectiveId,
      colorCode: priority.colorCode,
      htmlContent: content,
      startDate: null,
      endDate: null,
      value: objective.objectiveId,
      parentIds: parentIds
    };
    return new PriorityDropdownOption({
      label: objective.text,
      title: priority.title,
      value: value
    });
  }

  public static fromPriority(priority: StrtPriority): PriorityDropdownOption {
    const parentIds: IPriorityDropdownParentIds = {
      planId: 1,
      priorityId: priority.priorityId
    };
    const value: IPriorityDropdownOptionValue = {
      planId: 1,
      primaryId: priority.priorityId,
      colorCode: priority.colorCode,
      htmlContent: `${priority.description}`,
      startDate: null,
      endDate: null,
      value: priority.priorityId,
      parentIds: parentIds
    };
    return new PriorityDropdownOption({
      label: priority.description,
      title: priority.title,
      value: value
    });
  }

  public static fromPriorityInitiatives(priorities: StrtPriority[], boldedTitle: boolean = true):  PriorityDropdownOption[] {
    const obj: PriorityDropdownOption[] = [];
    priorities.forEach(p => {
      p.objectives.forEach(o => {
        o.initiatives.forEach(i => {
          obj.push(PriorityDropdownOption.fromInitiative(i, o, p, boldedTitle));
        });
      });
    });
    return obj;
  }

  public static fromPriorityObjectives(priorities: StrtPriority[], boldedTitle: boolean = true): PriorityDropdownOption[] {
    const obj: PriorityDropdownOption[] = [];
    priorities.forEach(p => {
      p.objectives.forEach(o => {
        obj.push(PriorityDropdownOption.fromObjective(o, p, boldedTitle));
      });
    });
    return obj;
  }

  public static fromPriorities(priorities: StrtPriority[]): PriorityDropdownOption[] {
    const obj: PriorityDropdownOption[] = [];
    priorities.forEach(p => {
      obj.push(PriorityDropdownOption.fromPriority(p));
    });
    return obj;
  }

  public static formattedDate(inDate: Date|string): string {
    try {
      const srcDate: Date = typeof inDate === 'string' ? (new Date(inDate)) : inDate;
      if (srcDate) {
        return `${srcDate.getMonth() + 1}/${srcDate.getDate()}/${srcDate.getFullYear()}`;
      }
    } catch (err) {
    }
    return '';
  }

  get hasDate(): boolean {
    return (this.value.startDate || this.value.endDate) && (this.value.startDate !== '' || this.value.endDate !== '');
  }
}
